import { memo, useState } from "react"
import { BsThreeDotsVertical } from "react-icons/bs"
import { FaEdit } from "react-icons/fa"
import { FiTrash2 } from "react-icons/fi"

import { Field } from "@/components/atoms/Field"
import { toaster } from "@/components/infrastructure"
import { useDeleteSession, useUpdateSession } from "@/hooks/api/useSessions"
import type { SessionResponse, SessionUpdateInput } from "@/types/session"
import { sessionUpdateSchema } from "@/types/session"
import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  IconButton,
  Input,
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
  Switch,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Controller,
  type Resolver,
  type SubmitHandler,
  useForm,
} from "react-hook-form"

interface SessionActionsMenuProps {
  session: SessionResponse
}

export const SessionActionsMenu = memo(
  ({ session }: SessionActionsMenuProps) => {
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)

    const deleteSessionMutation = useDeleteSession()
    const updateSessionMutation = useUpdateSession()

    const form = useForm<SessionUpdateInput>({
      resolver: zodResolver(
        sessionUpdateSchema,
      ) as Resolver<SessionUpdateInput>,
      mode: "onBlur",
      defaultValues: {
        name: session.name ?? "",
        slug: session.slug ?? "",
        description: session.description ?? "",
        session_type: session.session_type ?? null,
        start_week: session.start_week ?? "",
        end_week: session.end_week ?? "",
        registration_opens: session.registration_opens ?? "",
        registration_closes: session.registration_closes ?? "",
        early_bird_deadline: session.early_bird_deadline ?? "",
        makeup_classes_allowed: session.makeup_classes_allowed ?? null,
        no_class_dates: session.no_class_dates
          ? JSON.stringify(session.no_class_dates)
          : "",
        is_active: session.is_active ?? false,
        is_published: session.is_published ?? false,
        notes: session.notes ?? "",
      },
    })

    const getErrorText = (error: unknown): string | undefined => {
      if (typeof error === "object" && error !== null) {
        const maybeMessage = (error as { message?: unknown }).message
        if (typeof maybeMessage === "string") {
          return maybeMessage
        }
      }
      return undefined
    }

    const displayLabel = String(session.name ?? session.id)

    const handleDelete = async () => {
      try {
        await deleteSessionMutation.mutateAsync(session.id)
        setShowDeleteDialog(false)
        toaster.create({
          title: "Session deleted",
          description: `${displayLabel} has been deleted successfully`,
          type: "success",
        })
      } catch (error: any) {
        console.error("Error deleting session:", error)

        // Show user-friendly error message
        let errorMessage = "Failed to delete session"
        if (
          error?.message?.includes("Insufficient permissions") ||
          error?.status === 403
        ) {
          errorMessage = "You don't have permission to delete this session"
        } else if (error?.message) {
          errorMessage = error.message
        }

        toaster.create({
          title: "Delete failed",
          description: errorMessage,
          type: "error",
        })
      }
    }

    const handleUpdate: SubmitHandler<SessionUpdateInput> = async (data) => {
      try {
        await updateSessionMutation.mutateAsync({
          sessionId: session.id,
          sessionData: data,
        })
        setShowEditDialog(false)
        form.reset({
          name: session.name ?? "",
          slug: session.slug ?? "",
          description: session.description ?? "",
          session_type: session.session_type ?? null,
          start_week: session.start_week ?? "",
          end_week: session.end_week ?? "",
          registration_opens: session.registration_opens ?? "",
          registration_closes: session.registration_closes ?? "",
          early_bird_deadline: session.early_bird_deadline ?? "",
          makeup_classes_allowed: session.makeup_classes_allowed ?? null,
          no_class_dates: session.no_class_dates
            ? JSON.stringify(session.no_class_dates)
            : "",
          is_active: session.is_active ?? false,
          is_published: session.is_published ?? false,
          notes: session.notes ?? "",
        })
        toaster.create({
          title: "Session updated",
          description: `${displayLabel} has been updated successfully`,
          type: "success",
        })
      } catch (error: any) {
        console.error("Error updating session:", error)

        // Show user-friendly error message
        let errorMessage = "Failed to update session"
        if (
          error?.message?.includes("Insufficient permissions") ||
          error?.status === 403
        ) {
          errorMessage = "You don't have permission to edit this session"
        } else if (error?.message) {
          errorMessage = error.message
        }

        toaster.create({
          title: "Update failed",
          description: errorMessage,
          type: "error",
        })
      }
    }

    const handleEditClose = () => {
      setShowEditDialog(false)
      form.reset({
        name: session.name ?? "",
        slug: session.slug ?? "",
        description: session.description ?? "",
        session_type: session.session_type ?? null,
        start_week: session.start_week ?? "",
        end_week: session.end_week ?? "",
        registration_opens: session.registration_opens ?? "",
        registration_closes: session.registration_closes ?? "",
        early_bird_deadline: session.early_bird_deadline ?? "",
        makeup_classes_allowed: session.makeup_classes_allowed ?? null,
        no_class_dates: session.no_class_dates
          ? JSON.stringify(session.no_class_dates)
          : "",
        is_active: session.is_active ?? false,
        is_published: session.is_published ?? false,
        notes: session.notes ?? "",
      })
    }

    return (
      <>
        <MenuRoot>
          <MenuTrigger asChild>
            <IconButton
              variant="ghost"
              color="inherit"
              aria-label={`Actions for ${displayLabel}`}
            >
              <BsThreeDotsVertical />
            </IconButton>
          </MenuTrigger>
          <MenuContent>
            <MenuItem value="edit" onClick={() => setShowEditDialog(true)}>
              <FaEdit fontSize="14px" aria-hidden="true" />
              Edit
            </MenuItem>
            <MenuItem value="delete" onClick={() => setShowDeleteDialog(true)}>
              <FiTrash2 fontSize="14px" aria-hidden="true" />
              Delete
            </MenuItem>
          </MenuContent>
        </MenuRoot>

        {/* Edit Dialog */}
        <DialogRoot
          open={showEditDialog}
          onOpenChange={(e) => !e.open && handleEditClose()}
          size="md"
        >
          <DialogBackdrop />
          <DialogContent as="form" onSubmit={form.handleSubmit(handleUpdate)}>
            <DialogHeader>
              <DialogTitle>Edit Session</DialogTitle>
              <DialogCloseTrigger />
            </DialogHeader>

            <DialogBody>
              <VStack gap={6}>
                <Field
                  invalid={!!form.formState.errors.name}
                  errorText={getErrorText(form.formState.errors.name)}
                  label="Name"
                >
                  <Input
                    placeholder={"Enter name"}
                    aria-invalid={!!form.formState.errors.name}
                    {...form.register("name")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.slug}
                  errorText={getErrorText(form.formState.errors.slug)}
                  label="Slug"
                >
                  <Input
                    placeholder={"Enter slug"}
                    aria-invalid={!!form.formState.errors.slug}
                    {...form.register("slug")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.description}
                  errorText={getErrorText(form.formState.errors.description)}
                  label="Description"
                >
                  <Textarea
                    {...form.register("description")}
                    placeholder={"Enter description"}
                    rows={3}
                    resize="vertical"
                    aria-invalid={!!form.formState.errors.description}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.session_type}
                  errorText={getErrorText(form.formState.errors.session_type)}
                  label="Session Type"
                >
                  <select
                    {...form.register("session_type", {
                      setValueAs: (value) => (value === "" ? null : value),
                    })}
                    aria-invalid={!!form.formState.errors.session_type}
                    className="chakra-select"
                  >
                    {" "}
                    <option value="">Select Session Type</option>{" "}
                    <option value="regular_season">Regular Season</option>{" "}
                    <option value="summer_camp">Summer Camp</option>{" "}
                    <option value="winter_intensive">Winter Intensive</option>{" "}
                    <option value="spring_break">Spring Break</option>{" "}
                  </select>
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.start_week}
                  errorText={getErrorText(form.formState.errors.start_week)}
                  label="Start Date"
                >
                  <Input
                    type="date"
                    placeholder={"Select start date"}
                    aria-invalid={!!form.formState.errors.start_week}
                    {...form.register("start_week")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.end_week}
                  errorText={getErrorText(form.formState.errors.end_week)}
                  label="End Date"
                >
                  <Input
                    type="date"
                    placeholder={"Select end date"}
                    aria-invalid={!!form.formState.errors.end_week}
                    {...form.register("end_week")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.registration_opens}
                  errorText={getErrorText(
                    form.formState.errors.registration_opens,
                  )}
                  label="Registration Opens"
                >
                  <Input
                    type="datetime-local"
                    placeholder={"Enter registration opens"}
                    aria-invalid={!!form.formState.errors.registration_opens}
                    {...form.register("registration_opens")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.registration_closes}
                  errorText={getErrorText(
                    form.formState.errors.registration_closes,
                  )}
                  label="Registration Closes"
                >
                  <Input
                    type="datetime-local"
                    placeholder={"Enter registration closes"}
                    aria-invalid={!!form.formState.errors.registration_closes}
                    {...form.register("registration_closes")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.early_bird_deadline}
                  errorText={getErrorText(
                    form.formState.errors.early_bird_deadline,
                  )}
                  label="Early Bird Deadline"
                >
                  <Input
                    type="datetime-local"
                    placeholder={"Enter early bird deadline"}
                    aria-invalid={!!form.formState.errors.early_bird_deadline}
                    {...form.register("early_bird_deadline")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.makeup_classes_allowed}
                  errorText={getErrorText(
                    form.formState.errors.makeup_classes_allowed,
                  )}
                  label="Makeup Classes Allowed"
                >
                  <Input
                    type="number"
                    step={1}
                    placeholder={"Enter makeup classes allowed"}
                    aria-invalid={
                      !!form.formState.errors.makeup_classes_allowed
                    }
                    {...form.register("makeup_classes_allowed", {
                      valueAsNumber: true,
                    })}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.no_class_dates}
                  errorText={getErrorText(form.formState.errors.no_class_dates)}
                  label="No Class Dates"
                >
                  <Input
                    placeholder={"Enter no class dates"}
                    aria-invalid={!!form.formState.errors.no_class_dates}
                    {...form.register("no_class_dates")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.is_active}
                  errorText={getErrorText(form.formState.errors.is_active)}
                  label="Is Active"
                >
                  <Controller
                    name="is_active"
                    control={form.control}
                    render={({ field: controllerField }) => (
                      <Switch.Root
                        id="is_active"
                        checked={!!controllerField.value}
                        onCheckedChange={({ checked }) =>
                          controllerField.onChange(checked)
                        }
                        aria-label="Is Active"
                      >
                        <Switch.HiddenInput
                          ref={controllerField.ref}
                          name={controllerField.name}
                          onBlur={controllerField.onBlur}
                        />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    )}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.is_published}
                  errorText={getErrorText(form.formState.errors.is_published)}
                  label="Is Published"
                >
                  <Controller
                    name="is_published"
                    control={form.control}
                    render={({ field: controllerField }) => (
                      <Switch.Root
                        id="is_published"
                        checked={!!controllerField.value}
                        onCheckedChange={({ checked }) =>
                          controllerField.onChange(checked)
                        }
                        aria-label="Is Published"
                      >
                        <Switch.HiddenInput
                          ref={controllerField.ref}
                          name={controllerField.name}
                          onBlur={controllerField.onBlur}
                        />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    )}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.notes}
                  errorText={getErrorText(form.formState.errors.notes)}
                  label="Notes"
                >
                  <Textarea
                    {...form.register("notes")}
                    placeholder={"Enter notes"}
                    rows={3}
                    resize="vertical"
                    aria-invalid={!!form.formState.errors.notes}
                  />
                </Field>{" "}
              </VStack>
            </DialogBody>

            <DialogFooter>
              <HStack gap={3}>
                <Button
                  variant="ghost"
                  onClick={handleEditClose}
                  disabled={updateSessionMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorPalette="blue"
                  loading={updateSessionMutation.isPending}
                  loadingText="Updating..."
                >
                  Update Session
                </Button>
              </HStack>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>

        {/* Delete Dialog */}
        <DialogRoot
          open={showDeleteDialog}
          onOpenChange={(e) => !e.open && setShowDeleteDialog(false)}
          size="md"
        >
          <DialogBackdrop />
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Session</DialogTitle>
              <DialogCloseTrigger />
            </DialogHeader>

            <DialogBody>
              <Box
                bg="yellow.50"
                border="1px solid"
                borderColor="yellow.200"
                borderRadius="md"
                p={4}
                mb={4}
              >
                <Text color="yellow.800">
                  Are you sure you want to delete{" "}
                  <strong>"{displayLabel}"</strong>?
                </Text>
              </Box>

              <Text fontSize="sm" color="gray.600">
                This action cannot be undone. The session will be permanently
                removed from the system.
              </Text>
            </DialogBody>

            <DialogFooter>
              <HStack gap={3}>
                <Button
                  variant="ghost"
                  onClick={() => setShowDeleteDialog(false)}
                  disabled={deleteSessionMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  colorPalette="red"
                  onClick={handleDelete}
                  disabled={deleteSessionMutation.isPending}
                >
                  {deleteSessionMutation.isPending
                    ? "Deleting..."
                    : "Delete Session"}
                </Button>
              </HStack>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>
      </>
    )
  },
)

SessionActionsMenu.displayName = "SessionActionsMenu"

export default SessionActionsMenu
